import type { GapsViewFilterPreference } from "@/utils/models";
import type { GapsDayFilter } from "./gapsDayModel";

/**
 * How the gaps-first home's scope crosses into storage and back.
 *
 * A row the user never narrowed is `undefined` on screen and `null` in storage — "everything, whatever
 * that is next time" — and never a snapshot of today's list. Windows and units come from each week's
 * shifts, so a snapshot would hide the ones that appear later, their missing rows and their shortfall
 * with them, from a supervisor who only ever narrowed the job.
 */
export const toStoredFilter = (filter: GapsDayFilter): GapsViewFilterPreference => ({
	jobLabels: toStoredList(filter.jobLabels),
	locationNames: toStoredList(filter.locationNames),
	timeLabels: toStoredList(filter.timeLabels),
});

/** The stored scope, re-read against what the day actually has. */
export const fromStoredFilter = (
	stored: GapsViewFilterPreference,
	available: { jobLabels: string[]; locationNames: string[]; timeLabels: string[] },
): GapsDayFilter => ({
	jobLabels: toSelection(stored.jobLabels, available.jobLabels),
	locationNames: toSelection(stored.locationNames, available.locationNames),
	timeLabels: toSelection(stored.timeLabels, available.timeLabels),
});

const toStoredList = (selected: ReadonlySet<string> | undefined): string[] | null => (selected === undefined ? null : Array.from(selected));

/** A stored pick that still covers everything the day offers is "no filter", so the screen does not read as narrowed. */
const toSelection = (stored: string[] | null, available: string[]): ReadonlySet<string> | undefined =>
	stored === null || available.every((value) => stored.includes(value)) ? undefined : new Set(stored);
